import React, { useCallback, useMemo, MouseEvent } from 'react';
import { OutlinedTextFieldProps, Box, Tooltip } from '@mui/material';
import {
  isProjectFolder,
  isProjectInfo,
} from '@shared/helpers/project-folder.helpers';
import amplitudeLog from '@client/amplitude';
import { AMP_ELEMENT_NAME_UPDATE } from '@shared/constants';
import { useMouseEventCallback } from '@client/hooks/use-events.hooks';
import { useBooleanState } from '@client/hooks/use-utils.hooks';
import { useIsReadonly } from '@client/hooks/use-user.hooks';
import { useProjectState } from '@client/store/project';
import InlineTextField from '@ui/InlineTextField';
import { IProjectFolder } from '@shared/models/folder.model';
import { IProjectInfo } from '@shared/models/project.model';
import { useUIState } from '@client/store/ui';
import { useProjectListMap } from '../../../project-list-map.context';
import { getInputTextWidth } from '@client/helpers/dom.helpers';

interface ProjectListItemNameInputProps {
  item: IProjectFolder | IProjectInfo;
  isEditingName: boolean;
  autoFocus?: boolean;
  contentSized?: boolean;
  fontSize?: number;
  color?: string;
  onClick: (e: MouseEvent) => void | undefined;
  onCancel: () => void;
}

const ProjectListItemNameInput: React.FC<ProjectListItemNameInputProps> = ({
  item,
  autoFocus = false,
  contentSized = false,
  fontSize = 13,
  color = 'rgba(0,0,0,0.6)',
  isEditingName,
  onClick,
  onCancel,
}) => {
  const { name, id } = item;

  const { updateProjectDetails } = useProjectState('updateProjectDetails');
  const { setAddedElementId } = useUIState('setAddedElementId');

  const previewProject = useProjectListMap();
  const isFolder = isProjectFolder(item);
  const readonly = useIsReadonly();

  const [nameTooltipIsOpen, showNameTooltip, hideNameTooltip] =
    useBooleanState(false);

  const saveName = useCallback(
    async (name: string) => {
      onCancel();

      // Folders are retired; the list never enters edit mode for a folder row.
      if (isProjectFolder(item)) return;
      if (isProjectInfo(item)) {
        await updateProjectDetails({
          reference: parseInt(id),
          data: { name },
        });
      }

      amplitudeLog(AMP_ELEMENT_NAME_UPDATE, {
        Name: name,
        ElementID: id,
      });
    },
    [onCancel, item, id, updateProjectDetails],
  );

  const handleNameClick = useMouseEventCallback(
    async (e: MouseEvent) => {
      if (isFolder || isEditingName) {
        onClick(e);
        return;
      }
      hideNameTooltip();
      previewProject(id);
    },
    { ignoreInputEvents: true, ignoreOverlayEvents: true },
  );

  const nameInputProps: Partial<OutlinedTextFieldProps> = useMemo(
    () => ({
      autoFocus,
      onFocus: (e) => {
        if (autoFocus) {
          e.currentTarget.select();
        }
      },
      onBlur: () => {
        if (autoFocus) {
          setAddedElementId(undefined);
        }
      },
      sx: {
        cursor: isFolder ? 'auto' : 'pointer',
        ...(!isFolder && {
          '&:hover .MuiInputBase-input': {
            color: 'rgba(0,0,0,1) !important',
            WebkitTextFillColor: 'rgba(0,0,0,1) !important',
          },
          '& .MuiInputBase-input': {
            fontSize: `${fontSize}px !important`,
            fontWeight: '500 !important',
            ...(fontSize > 13 && { padding: '0 !important' }),
            color: `${color} !important`,
            WebkitTextFillColor: `${color} !important`,
          },
        }),
        width: contentSized && !isEditingName ? 'auto' : '100%',
        minWidth: 0,
        maxWidth: '100%',
      },
    }),
    [
      autoFocus,
      isFolder,
      setAddedElementId,
      contentSized,
      isEditingName,
      fontSize,
      color,
    ],
  );

  return (
    <Tooltip
      open={nameTooltipIsOpen}
      title={!isEditingName ? name : ''}
      onMouseOver={(event) => {
        const input =
          event.currentTarget.querySelector<HTMLInputElement>('input');
        if (!input || isEditingName) {
          hideNameTooltip();
          return;
        }
        const style = window.getComputedStyle(input);
        const availableWidth =
          Math.min(input.clientWidth, event.currentTarget.clientWidth) -
          parseFloat(style.paddingLeft) -
          parseFloat(style.paddingRight);
        if (getInputTextWidth(input, name) > availableWidth) {
          showNameTooltip();
        } else {
          hideNameTooltip();
        }
      }}
      onMouseOut={hideNameTooltip}
    >
      <Box
        data-project-opening-name=""
        aria-label={`Project: ${name}`}
        sx={{
          minWidth: 0,
          ...(contentSized &&
            !isEditingName && {
              flexShrink: 0,
              maxWidth:
                'calc(100% - var(--project-label-counter-width, 24px) - 2px)',
            }),
          width: contentSized && !isEditingName ? 'auto' : '100%',
          overflow: 'hidden',
        }}
      >
        <InlineTextField
          autoFocus
          variant="body2"
          value={name}
          editing={isEditingName}
          readonly={readonly || !isEditingName}
          allowClickEventWhenDisabled
          textFieldProps={nameInputProps}
          fullWidth={!contentSized || isEditingName}
          autoWidth={contentSized && !isEditingName}
          autoWidthMin={contentSized ? 0 : undefined}
          bold={isFolder}
          onSave={saveName}
          onCancel={onCancel}
          onClick={handleNameClick}
        />
      </Box>
    </Tooltip>
  );
};

export default ProjectListItemNameInput;
